import {useEffect, useMemo, useRef, useState} from "react";
import {useApp} from "../../app.context";
import StringInput from "../../elements/stringInput.element";
import {useIntegrator} from "../../integrator.context";
import {ChangeLocalizationValuesCommand} from "../../commands/changeLocalizationValues.command";
import {RemoveLocalizationValueCommand} from "../../commands/removeLocalizationValue.command";
import {ChangeLocalizationValueCommand} from "../../commands/changeLocalizationValue.command";
import {formatString} from "../../utils/text.utils";
import {getUsedLocalizationKeys} from "../../utils/localization.utils";

function LocalizationEditorView() {
    const {state, executeCommand} = useIntegrator();
    const {setNotification, localizationFocusKey, confirmDelete} = useApp();

    const [newKey, setNewKey] = useState<string>('');
    const focusRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (localizationFocusKey && focusRef.current) {
            focusRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [localizationFocusKey]);

    const languages = Object.keys(state.localization);
    const keys = Object.keys(state.localization[languages[0]]).sort((a, b) => a.localeCompare(b));
    const usedKeys = useMemo(() => getUsedLocalizationKeys(state), [state]);

    function addNewKey() {
        if (newKey === '') {
            setNotification({
                title: 'Invalid Input',
                message: 'Please enter a valid key.'
            })
            return;
        }

        if (newKey in state.localization['en']) {
            setNotification({
                title: 'Key Already Exists',
                message: 'Please enter a new key.'
            })
            return;
        }

        executeCommand(new ChangeLocalizationValuesCommand(newKey, 'New Value'));
        setNewKey('');
    }

    return (
        <div className={'layout vertical'} style={{gap: 10, flexGrow: 1}}>
            <div className={'layout vertical gap'} style={{height: 0, overflowY: 'auto', flexGrow: 1}}>
                {keys.map((key, i) => {
                    const languages = Object.keys(state.localization);
                    const isFocused = key === localizationFocusKey;
                    const isUnused = !usedKeys.has(key);
                    return (
                        <div className={'layout horizontal gap'}
                             ref={isFocused ? focusRef : undefined}
                             style={isFocused ? {
                                 backgroundColor: 'rgba(90, 154, 90, 0.3)',
                                 borderRadius: 4,
                                 padding: '2px 4px',
                                 transition: 'background-color 0.3s'
                             } : undefined}
                        >
                            <div key={i} className={'layout horizontal'}
                                 style={{color: isUnused ? 'var(--tint-color)' : 'white', alignItems: 'center', gap: 10, width: 360}}>
                                <button onClick={async () => {
                                    if (await confirmDelete(formatString(key), 'localization key'))
                                        executeCommand(new RemoveLocalizationValueCommand(key));
                                }}>
                                    <span className={'fa-solid fa-trash'}/>
                                </button>
                                <p key={i}
                                   style={{
                                       padding: '5.75px 0px',
                                       fontSize: 'var(--font-size-default)',
                                       flex: 1,
                                       minWidth: 0,
                                       wordBreak: 'break-all',
                                   }}
                                >
                                    {key}
                                </p>
                            </div>
                            {languages.map((language, index) => {
                                const v = state.localization[language][key];
                                return (
                                    <StringInput key={i} value={v}
                                                 style={{width: 240}}
                                                 onChange={(v) =>
                                                     executeCommand(new ChangeLocalizationValueCommand(keys[i], v, language))
                                                 }/>
                                )
                            })}
                        </div>
                    )
                })}
            </div>
            <input placeholder={'Enter key...'} value={newKey} onChange={v => setNewKey(v.target.value)}/>
            <button onClick={addNewKey}>Add New Key</button>
        </div>
    )
}

export default LocalizationEditorView;